import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { RouteProps, Route } from 'react-router-dom';
import { useLinkTo, useMount } from '../../../hooks';
import nonCriticalException from '../../../modules/exceptionLogger';
import { RouteGuardConfig } from '../../../react-routes';
import store from '../../../store';
import { IRootDispatch } from '../../../store/models';
import storeReady from '../../../store/ready';
import { requireLogin } from '../login/SignUpLoginModal';
import { AuthOrigins } from '../../../modules/user/constants';

type GuardableRouteProps = RouteProps & RouteGuardConfig;

export const GuardableRoute: React.FC<GuardableRouteProps> = ({
  loginFailedRedirect,
  loginRequired,
  loginOrigin,
  loginMessage,
  ...routeProps
}) => {
  const redirectTo = useLinkTo({ replace: true });
  const [canRender, setCanRender] = useState<boolean>(false);
  const dispatch = useDispatch<IRootDispatch>();

  useMount(async () => {
    if (loginRequired) {
      await storeReady;
      // if we're a direct land, we may not have loaded user information yet
      await dispatch.user.whoami({});
      const loggedInUserId = store.getState().user?.userId;

      if (!loggedInUserId) {
        dispatch.loader.setIsLoading(false);
        requireLogin({
          mode: 'clientlogin',
          origin: loginOrigin as AuthOrigins,
          message: loginMessage,
        })
          .then(({ userData }) => {
            if (!userData.user_id) {
              throw new Error(`${routeProps.path}: Successful login but user_id is undefined`);
            }
            setCanRender(true);
          })
          .catch(e => {
            nonCriticalException(
              `${routeProps.path}: Anon user cancelled login`,
              { ...(e?.msg && { message: e.msg }) },
            );
            redirectTo(loginFailedRedirect || 'home');
          });
      } else {
        // login is required, but there's already a user ID
        setCanRender(true);
      }
    } else {
      // always wait for store ready in guarded routes, regardless of user requirements
      setCanRender(true);
    }
  });

  return canRender && <Route {...routeProps} />;
};
